import SpaceForButton from '@/components/_common/SpaceForButtons';
import StatusTag from '@/components/_common/StatusTag';
import { InvoiceSituationColor } from '@/core/domain/invoice/invoice/Invoice.types';
import {
  Card,
  Row,
  Col,
  Typography,
  Button,
  Menu,
  Dropdown,
  message,
  notification,
  Modal,
  Alert,
  Empty,
  Popover,
} from 'antd';
import moment from 'moment';
import { useWorkorderDetailContext } from '../../WorkorderDetailProvider';
import {
  InvoiceDocumentType,
  InvoiceItemType,
  InvoiceOriginReferenceEntity,
  InvoiceRecipientType,
  InvoiceSituation,
  InvoiceType,
} from '@/core/domain/invoice/invoices/invoices.enums';
import { useInvoiceSettingsByCompany } from '@/core/domain/invoice/settings/Settings.hooks';
import { getWorkorderComplementNumber } from '@/utils/functions/getWorkorderComplementNumber';
import { flatInfiniteData } from '@/utils/request/flatInfiniteData';
import { useIntl } from 'react-intl';
import { COLUMNS_WIDTH } from '@/utils/table';
import { EditIcon, InfiniteTable } from '@/components/_common';
import { useWorkOrderComplementInvoiceValues } from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.hooks';
import { invoiceValuesModelsOptions } from '../../WorkOrderInvoice/WorkOrderInvoiceComplements';
import {
  InvoiceItem,
  InvoiceItemTaxes,
  InvoiceOriginCategory,
  InvoiceOriginReference,
} from '@/core/domain/invoice/invoices/invoices.types';
import {
  InvoiceValuesItemType,
  WorkOrderComplementStatus,
} from '@/core/domain/workorder/WorkOrdersComplements/WorkOrdersComplements.types';
import {
  useInvoiceCreateNFc,
  useInvoiceCreateNFe,
  useInvoiceCreateNFs,
  useInvoiceList,
} from '@/core/domain/invoice/invoices/invoices.hooks';
import { PersonType } from '@/core/domain/people/Customers/Customers.types';
import { useCallback, useState } from 'react';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { CustomerType } from '@/core/domain/workorder/WorkOrders/WorkOrders.types';
import { hasPermissions } from '@/utils/functions/hasPermissions';
import Authorities from '@/types/Authority';
import { queryClient } from '@/Root';
import useInvoiceFilesColumn from '@/components/_common/useInvoiceFilesColumn';
import { WorkOrderItemType } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.types';
import { useWorkOrderItems } from '@/core/domain/workorder/WorkOrdersItems/WorkOrdersItems.hooks';
import { WorkorderInvoiceValuesType } from '@/pages/MovementsPage/WorkOrdersPage/WorkOrderDetailsPage/InvoicePage';

enum InvoiceValueType {
  franchise = 'franchise',
  insurer = 'insurer',
  particular = 'particular',
}

const invoiceTypeColor: Record<string, string> = {
  [InvoiceType.NFC]: 'blue',
  [InvoiceType.NFE]: 'teal',
  [InvoiceType.NFS]: 'violet',
};

const invoiceValueLabels = {
  [InvoiceValueType.franchise]: 'Franquia',
  [InvoiceValueType.insurer]: 'Seguradora',
  [InvoiceValueType.particular]: 'Particular',
};

const WorkOrderComplementInvoice: React.FC = () => {
  const intl = useIntl();

  const {
    workorder: { data: workOrder },
    complement: { data: complement },
  } = useWorkorderDetailContext();

  const { data: complementItems, isLoading: complementItemLoading } =
    useWorkOrderItems(complement?.id);

  const invoiceFilesColumn = useInvoiceFilesColumn();

  const hasPart = complementItems?.data?.some(
    (item) => item.type === WorkOrderItemType.Part,
  );

  const hasService = complementItems?.data?.some(
    (item) => item.type === WorkOrderItemType.Service,
  );

  const { data: invoiceSettings } = useInvoiceSettingsByCompany(
    workOrder?.company.id,
  );

  const invoiceCreateNFs = useInvoiceCreateNFs();
  const invoiceCreateNFc = useInvoiceCreateNFc();
  const invoiceCreateNFe = useInvoiceCreateNFe();

  const invoicesData = useInvoiceList({
    companyId: workOrder?.company.id,
    entity: InvoiceOriginReferenceEntity.WORK_ORDER,
    entityId: complement?.id ? [complement?.id] : [],
  });

  const invoices = flatInfiniteData(invoicesData.data);

  const invoiceDataTable = invoices.map((invoice) => ({
    serie: invoice.serie,
    emissionDate: invoice.emissionDate,
    customerName: invoice?.customerName,
    supplierName: invoice?.supplierName,
    insurerName: invoice?.insurerName,
    id: invoice?.id,
    invoiceType: invoice?.invoiceType,
    number: invoice?.number,
    situation: invoice?.situation,
    isInternalApiIssued: invoice?.isInternalApiIssued,
  }));

  const workOrderComplementInvoiceValues =
    useWorkOrderComplementInvoiceValues();

  const [generateLoading, setGenerateLoading] = useState<{
    invoiceType: InvoiceType;
    recipientType: InvoiceValueType;
  }>();

  const hasInvoiceRecipientGenerated = useCallback(
    (invoiceType: InvoiceType, recipientType: InvoiceValueType) => {
      const invoicesCached = queryClient.getQueryData<typeof invoicesData.data>(
        [
          'invoices',
          {
            companyId: workOrder?.company.id,
            entity: InvoiceOriginReferenceEntity.WORK_ORDER,
            entityId: complement?.id ? [complement?.id] : [],
          },
        ],
      );

      const invoiceFlated = flatInfiniteData(invoicesCached);

      return invoiceFlated.some(
        (invoice) =>
          invoice.invoiceType === invoiceType &&
          invoice.situation !== InvoiceSituation.CANCELED &&
          invoice.originReference.some(
            (reference) =>
              reference.invoiceCategory === getInvoiceCategory(recipientType),
          ),
      );
    },
    [],
  );

  const getInvoiceCategory = (invoiceValuesType: InvoiceValueType) => {
    const types: Record<WorkorderInvoiceValuesType, InvoiceOriginCategory> = {
      particular: InvoiceOriginCategory.PRIVATE,
      insurer: InvoiceOriginCategory.INSURANCE,
      franchise: InvoiceOriginCategory.PRIVATE,
      porto: InvoiceOriginCategory.SUPPLIER,
    };

    return types[invoiceValuesType];
  };

  const generateInvoice = async (
    invoiceType: InvoiceType,
    recipientType: InvoiceValueType,
  ) => {
    setGenerateLoading({ invoiceType, recipientType });

    try {
      const hasInvoiceRecipient = hasInvoiceRecipientGenerated(
        invoiceType,
        recipientType,
      );

      if (hasInvoiceRecipient) {
        Modal.confirm({
          title: 'Nota fiscal já cadastrada!',
          content: `Este complemento já possui nota fiscal do tipo "${invoiceType}" gerada para "${invoiceValueLabels[recipientType]}"`,
          okButtonProps: { style: { display: 'none' } },
          cancelText: 'Fechar',
        });
        return;
      }

      if (workOrder && complement) {
        const model = invoiceValuesModelsOptions.find(
          (item) => item.invoiceType === invoiceType,
        )?.value;

        const invoiceValues =
          await workOrderComplementInvoiceValues.mutateAsync({
            workorderId: workOrder.id,
            complementsIds: [complement.id],
            model: model || 55,
          });

        const values = invoiceValues[recipientType];

        if (values) {
          const data = Object.values(values).filter(
            (item) =>
              !!item &&
              ((invoiceType === InvoiceType.NFS &&
                item.type === InvoiceValuesItemType.Service) ||
                (invoiceType !== InvoiceType.NFS &&
                  item.type === InvoiceValuesItemType.Part)),
          );

          if (!data.length) {
            message.warning(
              `Não há itens de "${invoiceValueLabels[recipientType]}" para gerar a nota fiscal de "${invoiceType}"`,
            );
          }

          await Promise.all(
            data?.map(async (dataItem) => {
              const originReference = dataItem.items.reduce((acc, item) => {
                if (!item.workOrderComplementId) {
                  return [
                    ...acc,
                    {
                      entity: InvoiceOriginReferenceEntity.WORK_ORDER,
                      entityCode: getWorkorderComplementNumber(
                        workOrder.number,
                        complement.number,
                      ),
                      entityId: complement.id,
                      workOrderId: workOrder.id,
                      invoiceCategory: getInvoiceCategory(recipientType),
                    },
                  ];
                } else if (
                  !acc.some((i) => i.entityId === item.workOrderComplementId)
                ) {
                  return [
                    ...acc,
                    {
                      entity: InvoiceOriginReferenceEntity.WORK_ORDER,
                      entityCode: getWorkorderComplementNumber(
                        workOrder.number,
                        item.workOrderComplementNumber,
                      ),
                      entityId: item.workOrderComplementId,
                      workOrderId: workOrder.id,
                      invoiceCategory: getInvoiceCategory(recipientType),
                    },
                  ];
                }
                return acc;
              }, [] as InvoiceOriginReference[]);

              const obj = {
                companyId: workOrder.company.id,
                originReference,
                recipientType:
                  recipientType === InvoiceValueType.particular
                    ? InvoiceRecipientType.CUSTOMER
                    : InvoiceRecipientType.INSURER,
                customerId:
                  recipientType !== InvoiceValueType.insurer
                    ? workOrder?.customer?.id
                    : undefined,
                insurerId:
                  recipientType === InvoiceValueType.insurer
                    ? complement?.insurer?.id
                    : undefined,
              };

              const invoiceItems = dataItem.items.map<InvoiceItem>((item) => ({
                quantity: item.quantity,
                type:
                  item.type === 'Service'
                    ? InvoiceItemType.TASK
                    : InvoiceItemType.PART,
                taskId: item.type === 'Service' ? item.id : undefined,
                partId: item.type === 'Part' ? item.id : undefined,
                valueUnitary: item.unitPrice,
                valueDiscount: item.valueDiscount,
                description: item.description,
              }));

              if (dataItem.type === InvoiceValuesItemType.Service) {
                return await invoiceCreateNFs.mutateAsync({
                  ...obj,
                  invoiceType: InvoiceType.NFS,
                  invoiceItem: invoiceItems.map((service) => {
                    const { valueDiscount, ...restValues } = service;

                    return {
                      ...restValues,

                      unconditionalDiscount:
                        typeof valueDiscount === 'number'
                          ? valueDiscount
                          : null,

                      itemTaxes: {
                        withheld: workOrder?.customer?.withheld,
                      } as InvoiceItemTaxes,
                    };
                  }),
                });
              } else if (dataItem.model === 65) {
                return await invoiceCreateNFc.mutateAsync({
                  ...obj,
                  finalConsumer:
                    dataItem.client.personType === PersonType.Natural,
                  invoiceItem: invoiceItems,
                  invoiceType,
                  documentType: InvoiceDocumentType.EXIT,
                });
              } else if (dataItem.model === 55) {
                return await invoiceCreateNFe.mutateAsync({
                  ...obj,
                  finalConsumer:
                    dataItem.client.personType === PersonType.Natural,
                  invoiceItem: invoiceItems,
                  invoiceType,
                  documentType: InvoiceDocumentType.EXIT,
                });
              }
            }),
          );
        } else {
          message.warning(
            `Não há dados de "${invoiceValueLabels[recipientType]}" para gerar a nota fiscal de "${invoiceType}"`,
          );
        }
      }
    } catch {
      notification.error({
        message: 'Erro no cadastro de nota fiscal',
        description: `Houve um erro ao criar a nota fiscal "${invoiceType}" de "${invoiceValueLabels[recipientType]}"`,
      });
    } finally {
      setGenerateLoading(undefined);
    }
  };

  const generateInvoiceConfirm = (
    invoiceType: InvoiceType,
    recipientType: InvoiceValueType,
  ) => {
    Modal.confirm({
      title: `Confirmação de criação de ${invoiceType}`,
      width: 600,
      content: (
        <>
          <p>{`Tem certeza que deseja criar uma nota fiscal de "${invoiceType}" do tipo "${invoiceValueLabels[recipientType]}"?`}</p>
          <Alert
            type="warning"
            description={`Será criado a nota fiscal referente aos itens do complemento "${getWorkorderComplementNumber(
              workOrder?.number,
              complement?.number,
            )}"`}
          />
        </>
      ),
      onOk: () => generateInvoice(invoiceType, recipientType),
      okText: 'Gerar nota fiscal',
    });
  };

  const menu = useCallback(
    (recipientType: InvoiceValueType) => (
      <Menu>
        {recipientType !== InvoiceValueType.franchise && (
          <>
            <Menu.Item
              onClick={() =>
                generateInvoiceConfirm(InvoiceType.NFE, recipientType)
              }
              disabled={!invoiceSettings?.issueNfe || !hasPart}
            >
              Gerar NF-e
            </Menu.Item>

            <Menu.Item
              onClick={() =>
                generateInvoiceConfirm(InvoiceType.NFC, recipientType)
              }
              disabled={!invoiceSettings?.issueNfc || !hasPart}
            >
              Gerar NFC-e
            </Menu.Item>
          </>
        )}

        <Menu.Item
          onClick={() => generateInvoiceConfirm(InvoiceType.NFS, recipientType)}
          disabled={
            !invoiceSettings?.issueNfs ||
            (recipientType !== InvoiceValueType.franchise && !hasService)
          }
        >
          Gerar NFS-e
        </Menu.Item>
      </Menu>
    ),
    [hasPart, hasService, invoiceSettings],
  );

  const dropdown = useCallback(
    (recipientType: InvoiceValueType, label: string) => {
      const noHasConfig =
        !invoiceSettings?.issueNfc &&
        !invoiceSettings?.issueNfe &&
        !invoiceSettings?.issueNfs;

      return (
        <Dropdown overlay={menu(recipientType)} disabled={noHasConfig}>
          <Popover
            content={
              noHasConfig ? 'Empresa sem configuração fiscal' : undefined
            }
          >
            <Button
              type="primary"
              disabled={noHasConfig}
              loading={complementItemLoading}
            >
              {label}
            </Button>
          </Popover>
        </Dropdown>
      );
    },
    [invoiceSettings, hasPart, hasService],
  );

  if (!hasPermissions([Authorities.FISCAL_INVOICE_VIEW])) {
    return null;
  }

  return (
    <>
      {!!generateLoading && (
        <ModalLoadingOverlay
          messages={[
            `Gerando "${generateLoading.invoiceType}" de "${
              invoiceValueLabels[generateLoading.recipientType]
            }"`,
          ]}
        />
      )}

      <Card style={{ borderRadius: 10 }}>
        <Row style={{ marginBottom: 24 }} gutter={[24, 24]}>
          <Col flex={1}>
            <Typography.Title level={4} style={{ marginBottom: 0 }}>
              Cadastro rápido de NF
            </Typography.Title>
          </Col>
          <Col>
            {(complement?.status === WorkOrderComplementStatus.Authorized ||
              complement?.status === WorkOrderComplementStatus.Closed) &&
              hasPermissions([Authorities.FISCAL_INVOICE_ADD]) && (
                <SpaceForButton>
                  {complement?.customerType === CustomerType.Insurance &&
                    dropdown(InvoiceValueType.franchise, 'NF Franquia')}
                  {complement?.customerType === CustomerType.Insurance &&
                    dropdown(InvoiceValueType.insurer, 'NF Seguradora')}
                  {complement?.customerType === CustomerType.Private &&
                    dropdown(InvoiceValueType.particular, 'NF Particular')}
                </SpaceForButton>
              )}
          </Col>
        </Row>

        {complement?.status !== WorkOrderComplementStatus.Authorized &&
          complement?.status !== WorkOrderComplementStatus.Closed && (
            <Empty description="Preencha os itens do complemento e Autorize a O.S." />
          )}

        {(complement?.status === WorkOrderComplementStatus.Authorized ||
          complement?.status === WorkOrderComplementStatus.Closed) && (
          <InfiniteTable
            pagination={false}
            dataSource={invoiceDataTable}
            loading={invoicesData.isLoading}
            columns={[
              {
                title: 'Tipo',
                dataIndex: 'invoiceType',
                width: COLUMNS_WIDTH.small,
                render: (value) => (
                  <StatusTag color={invoiceTypeColor[value]} label={value} />
                ),
              },
              {
                title: 'Situação',
                dataIndex: 'situation',
                width: COLUMNS_WIDTH.default,
                render: (_, { situation }) => {
                  if (situation) {
                    return (
                      <StatusTag
                        color={InvoiceSituationColor[situation]}
                        label={intl.formatMessage({
                          id: `invoice.situation.${situation}`,
                        })}
                      />
                    );
                  }
                  return '-';
                },
              },
              {
                title: 'Número',
                dataIndex: 'number',
                width: COLUMNS_WIDTH.default,
                render: (value) => value || '-',
              },
              {
                title: 'Série',
                dataIndex: 'serie',
                width: COLUMNS_WIDTH.small,
                render: (value) => value || '-',
              },
              {
                title: 'Dt. Emissão',
                dataIndex: 'emissionDate',
                width: COLUMNS_WIDTH.date,
                render: (value) =>
                  value ? moment(value).format('DD/MM/YYYY') : '-',
              },
              ...invoiceFilesColumn,
              {
                title: 'Ações',
                width: 70,
                align: 'right',
                fixed: 'right',
                render: (_, row) => (
                  <EditIcon
                    iconProps={{
                      onClick: () =>
                        window.open(
                          `/app/fiscal/invoice/${row.invoiceType}/${row.id}/datas`,
                          '_blank',
                        ),
                    }}
                  />
                ),
              },
            ]}
          />
        )}
      </Card>
    </>
  );
};

export default WorkOrderComplementInvoice;
